import { ENEMY_SALVAGEABLE_UNLOCK_LEVEL } from "@/config/constants";
import ChestRepository from "@/game/ChestRepository";
import { ResourceType } from "@/game/entities/config/resources";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import type { TileCoordinates } from "@/types/tile";
import {
	BuffType,
	TileAction,
	type TileConfig,
	type TileConfigItem,
	type UpgradeParams,
} from "@/types/tile";
import { randomInteger } from "@/utils/number";
import { FrameName } from ".";
import { ChestType } from "../objects/chests";
import { FX1AudioNames } from "./audio/audioConfig";

/** CONFIG **/

const config: TileConfig = {
	[FrameName.BANDIT_CAMP_SMALL]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 125,
					},
					skipPrice: {
						[ResourceType.GEMS]: 240,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.bandit_fight_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 45_000, max: 55_000 }],
			[ResourceType.IRON, { min: 5_000, max: 10_000 }],
			[ResourceType.GOODS, { min: 2_500, max: 5_000 }],
			[ResourceType.STEEL, { min: 2_000, max: 4_000 }],
		]),
		action: TileAction.DEFEAT,
		range: 2,
	},
	[FrameName.BANDIT_CAMP_MEDIUM]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 290,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.bandit_fight_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 190_000, max: 210_000 }],
			[ResourceType.IRON, { min: 11_000, max: 22_000 }],
			[ResourceType.GOODS, { min: 5_500, max: 11_000 }],
			[ResourceType.STEEL, { min: 4_400, max: 8_800 }],
		]),
		action: TileAction.DEFEAT,
		range: 3,
	},
	[FrameName.BANDIT_CAMP_LARGE]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 1250,
					},
					skipPrice: {
						[ResourceType.GEMS]: 340,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.bandit_fight_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 870_000, max: 930_000 }],
			[ResourceType.IRON, { min: 25_000, max: 50_000 }],
			[ResourceType.GOODS, { min: 12_500, max: 25_000 }],
			[ResourceType.STEEL, { min: 10_000, max: 20_000 }],
		]),
		action: TileAction.DEFEAT,
		range: 4,
	},
	[FrameName.DEPLETED_FIELD]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 4]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.LUMBER, { min: 25, max: 50 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 5) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 5 && r <= 10) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.ABANDONED_HUT]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 4]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 10_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.LUMBER, { min: 300, max: 500 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 10) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 10 && r <= 15) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.BURNED_HOUSE]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 9]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 25_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.LUMBER, { min: 500, max: 1_000 }],
			[ResourceType.STONE, { min: 500, max: 1_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 25) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 25 && r <= 35) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.DEEP_SEA_SMALL_PIRATES]: {
		exploreCoefficient: 6,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 250,
					},
					skipPrice: {
						[ResourceType.GEMS]: 240,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.naval_battle_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 95_000, max: 105_000 }],
			[ResourceType.IRON, { min: 1_000, max: 2_000 }],
			[ResourceType.GOODS, { min: 2_500, max: 5_000 }],
			[ResourceType.STEEL, { min: 2_000, max: 4_000 }],
		]),
		action: TileAction.DEFEAT,
		range: 3,
	},
	[FrameName.DEEP_SEA_MEDIUM_PIRATES]: {
		exploreCoefficient: 6,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 290,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.naval_battle_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 780_000, max: 820_000 }],
			[ResourceType.IRON, { min: 2_200, max: 4_400 }],
			[ResourceType.GOODS, { min: 5_500, max: 11_000 }],
			[ResourceType.STEEL, { min: 4_400, max: 8_800 }],
		]),
		action: TileAction.DEFEAT,
		range: 4,
	},
	[FrameName.OLD_BATTLEFIELD]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 20]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 50_000,
					},
					reward: {
						[ResourceType.IRON]: (): number =>
							randomInteger(1_000, 2_000),
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.IRON, { min: 1_000, max: 2_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			if (randomInteger(0, 100) <= 90) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			} else {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.GRASSLAND_CASTLE_RUINS]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 1]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 200_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.GEMS, { min: 10, max: 10 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			if (randomInteger(0, 100) <= 80) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			} else {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.HILLS_CASTLE_RUINS]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 1]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 200_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.GEMS, { min: 10, max: 10 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			if (randomInteger(0, 100) <= 80) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			} else {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.MERCENARY_CAMP]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 300_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.ARMY, { min: 25, max: 30 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			if (randomInteger(0, 100) <= 75) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			} else {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.HIRE,
	},
	[FrameName.MERCHANT_SHIP]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 25]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 100_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.GOODS, { min: 2_000, max: 3_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 5) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 5 && r <= 10) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.TRADING,
	},
	[FrameName.SHALLOW_SEA_SHIPWRECK]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 31]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 150_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.GOODS, { min: 2_000, max: 3_000 }],
			[ResourceType.STEEL, { min: 2_000, max: 3_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 5) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 5 && r <= 15) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.SHALLOW_SEA_ROCKS_SHIPWRECK]: {
		inSearchTool: true,
		exploreCoefficient: 2,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 31]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 150_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.GOODS, { min: 2_000, max: 3_000 }],
			[ResourceType.STEEL, { min: 2_000, max: 3_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 5) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 5 && r <= 15) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.MERCENARY_SHIP]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 500_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.ARMY, { min: 25, max: 45 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 5) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 5 && r <= 10) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.HIRE,
	},
	[FrameName.SHALLOW_SEA_SMALL_PIRATES]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 250,
					},
					skipPrice: {
						[ResourceType.GEMS]: 240,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.naval_battle_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 95_000, max: 105_000 }],
			[ResourceType.IRON, { min: 1_000, max: 2_000 }],
			[ResourceType.GOODS, { min: 2_500, max: 5_000 }],
			[ResourceType.STEEL, { min: 2_000, max: 4_000 }],
		]),
		action: TileAction.DEFEAT,
		range: 3,
	},
	[FrameName.SHALLOW_SEA_MEDIUM_PIRATES]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 290,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.naval_battle_oneshot,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 780_000, max: 820_000 }],
			[ResourceType.IRON, { min: 2_200, max: 4_400 }],
			[ResourceType.GOODS, { min: 5_500, max: 11_000 }],
			[ResourceType.STEEL, { min: 4_400, max: 8_800 }],
		]),
		action: TileAction.DEFEAT,
		range: 4,
	},
	[FrameName.TRAVELLING_MERCHANT]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 25]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.GOODS]: 10_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.SUPER_CLICKER, { min: 30, max: 30 }],
		]),
		action: TileAction.TRADING,
	},
	[FrameName.WOLFS_DEN]: {
		inSearchTool: true,
		exploreCoefficient: 1.2,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 10,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.animal_fight,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 50_000, max: 75_000 }],
		]),
		action: TileAction.CLEAR,
	},
	[FrameName.BEARS_DEN]: {
		inSearchTool: true,
		exploreCoefficient: 1.4,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 25,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.animal_fight,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 150_000, max: 350_000 }],
		]),
		action: TileAction.CLEAR,
	},
	[FrameName.WARRIORS_ENEMY]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		siegeAttributes: {
			maxHp: 2_500,
			armor: {
				melee: 1,
				ranged: 0,
				siege: 0,
			},
			attack: 6,
			grantBuffs: [{ type: BuffType.ADD_ARMOR_MELEE, value: 1 }],
		},
	},
	[FrameName.BARRICADE_ENEMY]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		siegeAttributes: {
			maxHp: 1000,
			armor: {
				melee: 0,
				ranged: 5,
				siege: 0,
			},
			attack: 2,
			grantBuffs: [{ type: BuffType.ADD_ARMOR_RANGED, value: 4 }],
		},
	},
	[FrameName.CAPTAIN_ENEMY]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		range: 3,
		siegeAttributes: {
			maxHp: 12_500,
			armor: {
				melee: 7,
				ranged: 5,
				siege: 0,
			},
			attack: 10,
			grantBuffs: [{ type: BuffType.BATTLE_FRENZY_ATTACK, value: 2 }],
		},
	},
	[FrameName.CHIEFTAIN_ENEMY]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		range: 4,
		siegeAttributes: {
			maxHp: 25_000,
			armor: {
				melee: 12,
				ranged: 8,
				siege: 0,
			},
			attack: 15,
			grantBuffs: [{ type: BuffType.BATTLE_FRENZY_ATTACK, value: 2 }],
		},
	},
	[FrameName.SHALLOW_SEA_RAIDERS_SKIFF]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		siegeAttributes: {
			maxHp: 5_000,
			armor: {
				melee: 1,
				ranged: 2,
				siege: 0,
			},
			attack: 4,
			grantBuffs: [
				{
					type: BuffType.ENGAGEMENT_ATTACK,
					value: 3,
				},
			],
		},
	},
	[FrameName.SHALLOW_SEA_RAIDERS_GALLEY]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		siegeAttributes: {
			maxHp: 7_500,
			armor: {
				melee: 2,
				ranged: 5,
				siege: 0,
			},
			attack: 8,
			grantBuffs: [
				{
					type: BuffType.ADD_ARMOR_RANGED,
					value: 2,
				},
			],
		},
	},
	[FrameName.SHALLOW_SEA_RAIDERS_FLAGSHIP]: {
		exploreCoefficient: 5,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, ENEMY_SALVAGEABLE_UNLOCK_LEVEL],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		action: TileAction.DEFEAT,
		range: 3,
		siegeAttributes: {
			maxHp: 20_000,
			armor: {
				melee: 3,
				ranged: 10,
				siege: 0,
			},
			attack: 12,
			grantBuffs: [
				{
					type: BuffType.BATTLE_FRENZY_ATTACK,
					value: 5,
				},
			],
		},
	},
	[FrameName.FLOTSAM_JETSAM]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: true,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 35]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 500_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.SUPER_CLICKER, { min: 5, max: 10 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 10) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 10 && r <= 15) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.DAMAGED_MILL]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 35]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.LUMBER, { min: 25_000, max: 100_000 }],
			[ResourceType.STONE, { min: 50_000, max: 75_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 25) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 25 && r <= 30) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.COLLAPSED_MINE]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 35]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 5_000_000,
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.IRON, { min: 50_000, max: 100_000 }],
			[ResourceType.STEEL, { min: 30_000, max: 50_000 }],
		]),
		onUpgrade: (upgradedTile: TileCoordinates): void => {
			const r = randomInteger(0, 100);
			if (r <= 10) {
				const chestrepo = ChestRepository.getInstance();
				if (!chestrepo.initialized) {
					chestrepo.initialize(useTileRepository());
				}
				chestrepo.tryGenerateChest(ChestType.EXPLORE, upgradedTile);
			}
			if (r > 10 && r <= 15) {
				const emitter = useEmitter();
				emitter.emit(EmitterEvents.NewSpecialLocation);
			}
		},
		action: TileAction.SAVE_MATERIAL,
	},
	[FrameName.HORNETS_NEST]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		isVisible: false,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 40]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.ARMY]: 100,
					},
					onUpgrade: (): void => {
						SoundService.getInstance().playFX1(
							FX1AudioNames.animal_fight,
						);
					},
				},
			],
		]),
		rewardRange: new Map<ResourceType, { min: number; max: number }>([
			[ResourceType.COINS, { min: 500_000, max: 750_000 }],
		]),
		action: TileAction.CLEAR,
	},
};

export const getSalvageableConfig = (frameName: FrameName): TileConfigItem => {
	const salvageableConfig = config?.[frameName];
	if (!salvageableConfig)
		throw new Error(`Salvageable config not found for type: ${frameName}!`);

	return salvageableConfig;
};

export const isSalvageable = (frameName: FrameName): boolean =>
	frameName in config;

export const getAllSalvageablesForSearchTool = (): Array<FrameName> => {
	const salvagables: Array<FrameName> = [];
	for (const [frameName, salvageableConfig] of Object.entries(config)) {
		if (salvageableConfig.inSearchTool) {
			salvagables.push(frameName as FrameName);
		}
	}
	return salvagables;
};
